import React, { useEffect, useState } from "react";
import { FormLabel } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { OtpReason } from "muminsmart-utils";
import { toast } from "sonner";
import { sendOtp, validateOtp } from "@/lib/api/auth-actions";

type ContactChannel = "email" | "mobile";

export interface OtpVerificationState {
  isVerified: boolean;
  verifiedOtp: string | null;
  value: string;
}

interface ContactOtpVerificationProps {
  label: string;
  value: string;
  reason: OtpReason;
  channel: ContactChannel;
  children: React.ReactNode;
  disabled?: boolean;
  otpLength?: number;
  verifiedText?: string;
  defaultVerifiedValue?: string;
  onValidateField?: () => Promise<boolean> | boolean;
  onVerifiedStateChange?: React.Dispatch<
    React.SetStateAction<OtpVerificationState>
  >;
}

export const normalizePhoneNumber = (phoneNumber: string) => {
  const digitsOnly = phoneNumber.replace(/\D/g, "");
  return digitsOnly ? `+${digitsOnly}` : "";
};

export const normalizeEmail = (email: string) => email.trim().toLowerCase();

const RESEND_COOLDOWN_SECONDS = 120;

const formatCountdown = (totalSeconds: number) => {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
};

function ContactOtpVerification({
  label,
  value,
  reason,
  channel,
  children,
  disabled = false,
  otpLength = 6,
  verifiedText,
  defaultVerifiedValue,
  onValidateField,
  onVerifiedStateChange,
}: ContactOtpVerificationProps) {
  const [otp, setOtp] = useState("");
  const [otpSentForValue, setOtpSentForValue] = useState<string | null>(null);
  const [verifiedOtpsByValue, setVerifiedOtpsByValue] = useState<
    Record<string, string>
  >(() => (defaultVerifiedValue ? { [defaultVerifiedValue]: "DEFAULT" } : {}));
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [resendSecondsLeft, setResendSecondsLeft] = useState(0);
  const [otpSendCount, setOtpSendCount] = useState(0);

  const isCurrentValueVerified = Boolean(value && verifiedOtpsByValue[value]);
  const currentValueOtp = value ? verifiedOtpsByValue[value] : undefined;

  useEffect(() => {
    if (!value || otpSentForValue === value) {
      return;
    }

    setOtp("");
  }, [value, otpSentForValue]);

  // otpSendCount changes on every send/resend, even when otpSentForValue stays
  // the same, so the cooldown restarts each time.
  useEffect(() => {
    if (otpSentForValue !== value || isCurrentValueVerified || !otpSendCount) {
      return;
    }

    setResendSecondsLeft(RESEND_COOLDOWN_SECONDS);

    const timer = setInterval(() => {
      setResendSecondsLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [otpSentForValue, value, isCurrentValueVerified, otpSendCount]);

  useEffect(() => {
    if (!onVerifiedStateChange) {
      return;
    }

    onVerifiedStateChange({
      isVerified: isCurrentValueVerified,
      verifiedOtp: currentValueOtp || null,
      value,
    });
  }, [isCurrentValueVerified, currentValueOtp, onVerifiedStateChange, value]);

  const handleSendOtp = async () => {
    const isValid = onValidateField
      ? await Promise.resolve(onValidateField())
      : true;

    if (!isValid) {
      return;
    }

    if (!value) {
      toast.error(`Please enter a valid ${label.toLowerCase()}.`);
      return;
    }

    if (isCurrentValueVerified) {
      toast.success(`This ${label.toLowerCase()} is already verified.`);
      return;
    }

    setIsSendingOtp(true);

    sendOtp({
      [channel]: value,
      reason,
    })
      .then((res) => {
        if (res.success) {
          setOtpSentForValue(value);
          setOtpSendCount((prev) => prev + 1);
          setOtp("");
          toast.success(res.message || "OTP sent successfully.");
        } else {
          toast.error(res.message || "Failed to send OTP.");
        }
      })
      .catch((error) => toast.error(error.message))
      .finally(() => setIsSendingOtp(false));
  };

  const handleVerifyOtp = async () => {
    if (!otpSentForValue || otpSentForValue !== value) {
      toast.error(
        `Please send OTP for the current ${label.toLowerCase()} first.`,
      );
      return;
    }

    if (!otp || otp.length !== otpLength) {
      toast.error(`OTP must be ${otpLength} digits.`);
      return;
    }

    setIsVerifyingOtp(true);

    validateOtp({
      [channel]: value,
      reason,
      otp,
    })
      .then((res) => {
        if (res.success) {
          setVerifiedOtpsByValue((prev) => ({
            ...prev,
            [value]: otp,
          }));
          toast.success(`${label} verified.`);
        } else {
          toast.error(res.message || "Failed to verify OTP.");
        }
      })
      .catch((error) => toast.error(error.message))
      .finally(() => setIsVerifyingOtp(false));
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <FormLabel className="font-medium">{label}</FormLabel>
        {otpSentForValue !== value && !isCurrentValueVerified && (
          <Button
            type="button"
            size="sm"
            className="rounded-full bg-primary"
            onClick={handleSendOtp}
            disabled={disabled || isSendingOtp || Boolean(!value)}
          >
            {isSendingOtp ? "Sending..." : "Send OTP"}
          </Button>
        )}
      </div>

      {children}

      {otpSentForValue === value && !isCurrentValueVerified && (
        <div className="space-y-2 rounded-3xl border border-muted bg-white p-3 text-center">
          <div className="flex gap-1 justify-center items-center flex-wrap">
            <InputOTP
              maxLength={otpLength}
              value={otp}
              onChange={setOtp}
              className="mx-auto w-full"
            >
              <InputOTPGroup>
                {Array.from({ length: otpLength }).map((_, idx) => (
                  <InputOTPSlot key={idx} index={idx} />
                ))}
              </InputOTPGroup>
            </InputOTP>
          </div>
          <div className="flex gap-2 justify-center items-center flex-wrap">
            <Button
              type="button"
              size="sm"
              className="rounded-full"
              onClick={handleVerifyOtp}
              disabled={disabled || isVerifyingOtp}
            >
              {isVerifyingOtp ? "Verifying..." : "Verify"}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="rounded-full"
              onClick={handleSendOtp}
              disabled={disabled || isSendingOtp || resendSecondsLeft > 0}
            >
              {resendSecondsLeft > 0
                ? `Resend in ${formatCountdown(resendSecondsLeft)}`
                : "Resend"}
            </Button>
          </div>
        </div>
      )}

      {isCurrentValueVerified && (
        <p className="text-sm font-medium text-green-600">
          {verifiedText || `${label} verified`}
        </p>
      )}
    </div>
  );
}

export default ContactOtpVerification;
